export interface ChargingStation {
  id: string;
  stationId: string;
  name: string;
  vendorName?: string;
  model?: string;
  serialNumber?: string;
  firmwareVersion?: string;
  status: StationStatus;
  connectors: Connector[];
  location?: Location;
  lastHeartbeat?: string;
  configuration?: Record<string, any>;
  isOnline: boolean;
  wsSessionId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface Connector {
  connectorId: number;
  type: ConnectorType;
  status: ConnectorStatus;
  maxPower: number; // in Watts
  currentPower: number; // in Watts
  errorCode?: string;
  info?: string;
  statusUpdatedAt?: string;
}

export interface Location {
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  country?: string;
  postalCode?: string;
}

export interface ChargingSession {
  id: string;
  sessionId: string;
  stationId: string;
  connectorId: number;
  transactionId: string;
  idTag: string;
  startTime: string;
  endTime?: string;
  status: SessionStatus;
  energyConsumed: number; // in kWh
  currentPower: number; // in Watts
  totalCost: number;
  meterStart: number;
  meterStop?: number;
  stopReason?: StopReason;
  meterValues?: MeterValue[];
  reservationId?: string;
  chargingProfile?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MeterValue {
  timestamp: string;
  sampledValues: SampledValue[];
}

export interface SampledValue {
  value: number;
  context?: string;
  measurand?: string;
  phase?: string;
  location?: string;
  unit?: string;
}

export interface OCPPMessage {
  id: string;
  messageId: string;
  stationId: string;
  sessionId?: string;
  messageType: MessageType;
  direction: Direction;
  action: string;
  payload?: Record<string, any>;
  response?: Record<string, any>;
  errorCode?: string;
  errorDescription?: string;
  processed: boolean;
  processingTimeMs?: number;
  timestamp: string;
}

export enum StationStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  UNAVAILABLE = 'UNAVAILABLE',
  FAULTED = 'FAULTED',
  OFFLINE = 'OFFLINE'
}

export enum ConnectorStatus {
  AVAILABLE = 'AVAILABLE',
  PREPARING = 'PREPARING',
  OCCUPIED = 'OCCUPIED',
  RESERVED = 'RESERVED',
  UNAVAILABLE = 'UNAVAILABLE',
  FAULTED = 'FAULTED'
}

export enum ConnectorType {
  CCS = 'CCS',
  CHADEMO = 'CHADEMO',
  TYPE2 = 'TYPE2',
  TYPE1 = 'TYPE1',
  GB_T = 'GB_T',
  OTHER = 'OTHER'
}

export enum SessionStatus {
  STARTED = 'STARTED',
  CHARGING = 'CHARGING',
  SUSPENDED_EV = 'SUSPENDED_EV',
  SUSPENDED_EVSE = 'SUSPENDED_EVSE',
  STOPPED = 'STOPPED',
  FAULTED = 'FAULTED'
}

export enum StopReason {
  EV_DISCONNECTED = 'EV_DISCONNECTED',
  LOCAL = 'LOCAL',
  REMOTE = 'REMOTE',
  EMERGENCY_STOP = 'EMERGENCY_STOP',
  OTHER = 'OTHER',
  POWER_LOSS = 'POWER_LOSS',
  REBOOT = 'REBOOT',
  DE_AUTHORIZED = 'DE_AUTHORIZED',
  ENERGY_LIMIT_REACHED = 'ENERGY_LIMIT_REACHED',
  GROUND_FAULT = 'GROUND_FAULT',
  IMMEDIATE_RESET = 'IMMEDIATE_RESET',
  LOCAL_OUT_OF_CREDIT = 'LOCAL_OUT_OF_CREDIT',
  MASTER_PASS = 'MASTER_PASS',
  OVER_CURRENT_FAULT = 'OVER_CURRENT_FAULT',
  POWER_QUALITY = 'POWER_QUALITY',
  SOC_LIMIT_REACHED = 'SOC_LIMIT_REACHED',
  STOPPED_BY_EV = 'STOPPED_BY_EV',
  TIME_LIMIT_REACHED = 'TIME_LIMIT_REACHED',
  TIMEOUT = 'TIMEOUT'
}

export enum MessageType {
  CALL = 2,
  CALLRESULT = 3,
  CALLERROR = 4
}

export enum Direction {
  REQUEST = 'REQUEST',
  RESPONSE = 'RESPONSE'
}

export interface DashboardStats {
  totalStations: number;
  onlineStations: number;
  offlineStations: number;
  stationAvailability: number;
  totalSessions: number;
  activeSessions: number;
  totalEnergyConsumed: number;
  averageEnergyPerSession: number;
}

export interface SimulatedData {
  energyActiveImportRegister: number;
  powerActiveImport: number;
  currentImport: number;
  voltage: number;
}